import {
  CreditCard,
  FileCheck,
  FileText,
  IndianRupee,
  Minus,
  Plus,
  Printer,
  Receipt,
  Search,
  ShoppingBag,
  Trash2,
  User,
  X,
  Bluetooth,
} from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useWirelessPrinter } from '@/features/hardware/hooks/useWirelessPrinter';
import { compileWirelessReceipt } from '@/features/hardware/utils/compileWirelessReceipt';

interface CartItem {
  id: string;
  productId?: string;
  variantId?: string;
  name: string;
  sku: string;
  price: number;
  quantity: number;
  discount: number; // percentage
  gstRate: number; // percentage (e.g. 5, 12, 18, 28)
  mrp?: number;
  discount_percentage?: number;
  retail_price?: number;
  wholesale_price?: number;
}

// ─── Tenant Profile & Tax Slab Types ─────────────────────────────────────────

interface TaxSlab {
  threshold_amount: number;
  low_rate_percentage: number;
  high_rate_percentage: number;
}

interface TenantProfile {
  company_name: string;
  company_address: string;
  company_mobile: string;
  is_gst_eligible: boolean;
  tenant_gstin: string | null;
  tax_slabs: TaxSlab[];
  print_config?: {
    connection_mode?: 'BLUETOOTH' | 'USB';
    footer_message?: string;
    paper_size?: '58mm' | '80mm';
  };
}

interface HeldBill {
  id: string;
  timestamp: string;
  customerName: string;
  customerPhone: string;
  customerGstin: string;
  cart: CartItem[];
  gstType: 'CGST_SGST' | 'IGST';
  paymentMode: 'CASH' | 'CARD' | 'UPI';
  overallDiscount: number;
}

export function BillingDashboard() {
  const [searchParams] = useSearchParams();
  const modeParam = searchParams.get('mode');
  const { user } = useAuth();

  const {
    isConnected: isPrinterConnected,
    isPrinting: isPrinterPrinting,
    pairedDevice,
    error: printerError,
    connectPrinter,
    sendPrintJob,
  } = useWirelessPrinter();

  // ─── Tenant profile & GST config ────────────────────────────
  const [tenantProfile, setTenantProfile] = useState<TenantProfile>({
    company_name: '',
    company_address: '',
    company_mobile: '',
    is_gst_eligible: false,
    tenant_gstin: null,
    tax_slabs: [],
  });

  const tenantFlags = JSON.parse(
    localStorage.getItem(`tenant_flags_${user?.tenantId}`) ||
    '{"bulkMatrix":false,"gstComplianceExport":false,"loyaltyEngine":false}',
  );
  const isGstEnabled = !!(tenantFlags.gstComplianceExport && tenantProfile.is_gst_eligible);

  const [activeTab, setActiveTab] = useState<'TERMINAL' | 'DRAFTS' | 'HISTORY'>(
    modeParam === 'terminal' ? 'TERMINAL' : 'HISTORY',
  );

  useEffect(() => {
    if (modeParam === 'terminal') {
      setActiveTab('TERMINAL');
    } else {
      setActiveTab('HISTORY');
    }
  }, [modeParam]);

  // Fetch tenant company profile to drive GST engine and receipt header
  useEffect(() => {
    if (!user?.tenantId) return;
    api
      .get('/tenant/settings/company-profile', {
        headers: { 'x-tenant-id': user.tenantId },
      })
      .then((r: any) => setTenantProfile(r.data.data))
      .catch(() => {/* Use defaults on error */ });
  }, [user?.tenantId]);

  const [productsList, setProductsList] = useState<any[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  const fetchProducts = async () => {
    if (!user?.tenantId) return;
    setLoadingProducts(true);
    try {
      const headers = { 'x-tenant-id': user.tenantId };
      const response = await api.get('/inventory/products', { headers });
      const dbProducts: any[] = [];
      response.data.data.forEach((p: any) => {
        if (p.variants && p.variants.length > 0) {
          p.variants.forEach((v: any) => {
            dbProducts.push({
              id: v.id,
              productId: p.id,
              variantId: v.id,
              name: `${p.name} (${v.color || 'N/A'} / ${v.size || 'N/A'})`,
              sku: v.sku,
              barcode: v.barcode,
              price: v.retail_price || 0,
              mrp: v.mrp || 0,
              discount_percentage: v.discount_percentage || 0,
              wholesale_price: v.wholesale_price || 0,
              gstRate: p.gstRate || 12
            });
          });
        } else {
          dbProducts.push({
            id: p.id,
            productId: p.id,
            variantId: undefined,
            name: p.name,
            sku: p.sku,
            barcode: p.barcode,
            price: p.sellingPrice || 0,
            mrp: p.mrp || p.sellingPrice || 0,
            discount_percentage: 0,
            wholesale_price: p.sellingPrice || 0,
            gstRate: p.gstRate || 12
          });
        }
      });
      setProductsList(dbProducts);
    } catch (err) {
      console.error('Failed to retrieve catalog products:', err);
      setProductsList([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [activeTab]);

  // Global audio confirmation beep utility (sine wave synthesizer)
  const playBeep = () => {
    try {
      const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);

      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(1000, audioCtx.currentTime); // 1000 Hz tone
      gainNode.gain.setValueAtTime(0.08, audioCtx.currentTime); // subtle volume

      oscillator.start();
      setTimeout(() => {
        oscillator.stop();
        audioCtx.close();
      }, 75); // short 75ms beep
    } catch (err) {
      console.warn('Audio beep failed:', err);
    }
  };

  // High-Speed Global Barcode Scanner Listener
  useEffect(() => {
    let buffer = '';
    let lastKeyTime = Date.now();

    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      const currentTime = Date.now();
      const timeDiff = currentTime - lastKeyTime;
      lastKeyTime = currentTime;

      // Reset buffer if delay between keystrokes is human typing speed (>50ms)
      if (timeDiff > 50) {
        buffer = '';
      }

      if (e.key.length === 1) {
        buffer += e.key;
      } else if (e.key === 'Enter') {
        const cleanQuery = buffer.trim().toUpperCase();
        if (cleanQuery.length > 2) {
          const matched = productsList.find((p: any) => 
            p.sku.toUpperCase() === cleanQuery || 
            (p.barcode && p.barcode.toUpperCase() === cleanQuery)
          );

          if (matched) {
            addToCart(matched);
            playBeep();

            // Intercept focused input to strip typed barcode
            const target = e.target as HTMLElement;
            if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
              const input = target as HTMLInputElement;
              const val = input.value;
              const uppercaseVal = val.toUpperCase();
              if (uppercaseVal.endsWith(cleanQuery)) {
                const startIdx = uppercaseVal.lastIndexOf(cleanQuery);
                input.value = val.substring(0, startIdx);
                // Dispatch input event to sync state bindings in React
                const event = new Event('input', { bubbles: true });
                input.dispatchEvent(event);
              }
            }

            buffer = '';
            e.preventDefault();
            e.stopPropagation();
          }
        }
        buffer = '';
      }
    };

    // Use capture flag to intercept keydowns before input defaults fire
    window.addEventListener('keydown', handleGlobalKeyDown, true);
    return () => {
      window.removeEventListener('keydown', handleGlobalKeyDown, true);
    };
  }, [productsList]);

  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const [searchError, setSearchError] = useState('');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Auto-focus search bar when entering POS Terminal view
  useEffect(() => {
    if (activeTab === 'TERMINAL') {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [activeTab]);

  // Customer info
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerGstin, setCustomerGstin] = useState('');
  const [customerCity, setCustomerCity] = useState('');
  const [customerSuggestions, setCustomerSuggestions] = useState<any[]>([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [gstType, setGstType] = useState<'CGST_SGST' | 'IGST'>('CGST_SGST');
  const [paymentMode, setPaymentMode] = useState<'CASH' | 'CARD' | 'UPI'>('CASH');

  // Overall discount summary
  const [overallDiscount, setOverallDiscount] = useState<number>(0);

  // Parking Lot list state for "Held Drafts"
  const [heldBills, setHeldBills] = useState<HeldBill[]>([]);

  // Receipt Modal State
  const [showReceipt, setShowReceipt] = useState(false);
  const [isReprint, setIsReprint] = useState(false);

  // Exchange Credit Token states
  const [crmTokenSuggestion, setCrmTokenSuggestion] = useState<{ code: string; balance: number } | null>(null);
  const [tokenInput, setTokenInput] = useState('');
  const [appliedToken, setAppliedToken] = useState<any | null>(null);
  const [tokenError, setTokenError] = useState('');
  const [tokenSuccess, setTokenSuccess] = useState('');
  const [generatedInvoice, setGeneratedInvoice] = useState<any>(null);

  // Invoices list state (fetched from backend)
  const [invoices, setInvoices] = useState<any[]>([]);

  // Load invoices from backend
  const fetchInvoices = async () => {
    if (!user?.tenantId) return;
    try {
      const response = await api.get('/billing/invoices', {
        headers: { 'x-tenant-id': user.tenantId },
      });
      if (response.data.success) {
        const dbInvoices = response.data.data.map((inv: any) => {
          const discountTotal = inv.discountTotal || 0;
          const subtotal = inv.subtotal || 0;
          const overallDiscountPercent = subtotal > 0 ? Math.round((discountTotal / subtotal) * 100) : 0;
          const cgstTotal = inv.cgstTotal || 0;
          const sgstTotal = inv.sgstTotal || 0;
          const igstTotal = inv.igstTotal || 0;
          const totalGst = cgstTotal + sgstTotal + igstTotal;

          return {
            ...inv,
            id: inv.invoiceNumber || inv.id,
            date: inv.invoiceDate || inv.createdAt,
            customerName: inv.customerName,
            customerPhone: inv.customerPhone || 'N/A',
            customerGstin: inv.customerGstin || undefined,
            subtotal: subtotal,
            overallDiscount: overallDiscountPercent,
            overallDiscountAmount: discountTotal,
            netSubtotal: subtotal - discountTotal,
            cgstTotal,
            sgstTotal,
            igstTotal,
            totalGst,
            gstType: cgstTotal > 0 ? 'CGST_SGST' : 'IGST',
            grandTotal: inv.grandTotal,
            paymentMode: inv.paymentMode,
            status: inv.status,
            items: (inv.items || []).map((item: any) => ({
              ...item,
              name: item.productName || item.name,
              price: item.unitPrice !== undefined ? item.unitPrice : item.price,
            })),
            loyaltyPointsEarned: inv.loyaltyPointsEarned || 0
          };
        });
        setInvoices(dbInvoices);
      }
    } catch (err) {
      console.error('Failed to fetch invoices:', err);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, [activeTab, user?.tenantId]);

  // Auto-lookup hook: when phone is a valid 10-digit Indian number, search customer directory
  useEffect(() => {
    const phone = customerPhone.trim();
    if (!user?.tenantId || !/^[6-9]\d{9}$/.test(phone)) {
      return;
    }

    const lookupCustomer = async () => {
      try {
        const response = await api.get('/tenant/customers/lookup', {
          params: { mobile: phone },
          headers: { 'x-tenant-id': user.tenantId },
        });
        if (response.data.success && response.data.data) {
          const c = response.data.data;
          setCustomerName(c.name || '');
          setCustomerGstin(c.customerGstin || '');
          setCustomerCity(c.cityOrPlace || '');

          if (c.exchangeTokens && c.exchangeTokens.length > 0) {
            const activeToken = c.exchangeTokens[0];
            setCrmTokenSuggestion({
              code: activeToken.tokenCode,
              balance: activeToken.balanceRemaining
            });
          } else {
            setCrmTokenSuggestion(null);
          }
        } else {
          setCrmTokenSuggestion(null);
        }
      } catch (err) {
        console.error('Customer lookup failed:', err);
        setCrmTokenSuggestion(null);
      }
    };

    lookupCustomer();
  }, [customerPhone, user?.tenantId]);

  // Autocomplete search hook: when phone is typed digits one-by-one, search partial matches
  useEffect(() => {
    const phone = customerPhone.trim();
    if (!user?.tenantId || phone.length < 3) {
      setCustomerSuggestions([]);
      setShowCustomerDropdown(false);
      return;
    }

    const searchCustomers = async () => {
      try {
        const response = await api.get('/tenant/customers', {
          params: { search: phone, limit: 5 },
          headers: { 'x-tenant-id': user.tenantId },
        });
        if (response.data.success && response.data.customers) {
          setCustomerSuggestions(response.data.customers);
          setShowCustomerDropdown(response.data.customers.length > 0);
        } else {
          setCustomerSuggestions([]);
          setShowCustomerDropdown(false);
        }
      } catch (err) {
        console.error('Failed to search customers:', err);
      }
    };

    const delayDebounce = setTimeout(() => {
      searchCustomers();
    }, 200);

    return () => clearTimeout(delayDebounce);
  }, [customerPhone, user?.tenantId]);

  const handleApplyToken = async (codeToApply?: string) => {
    const code = (codeToApply || tokenInput).trim();
    if (!code) return;
    setTokenError('');
    setTokenSuccess('');

    try {
      const headers = { 'x-tenant-id': user?.tenantId };
      const res = await api.get(`/returns/validate-token?code=${code}`, { headers });
      if (res.data.success && res.data.data) {
        const token = res.data.data;
        setAppliedToken(token);
        setTokenInput(token.tokenCode);
        setTokenSuccess(`Applied exchange token credit of ₹${token.balanceRemaining.toFixed(2)}`);
      }
    } catch (err: any) {
      setTokenError(err.response?.data?.message || 'Failed to validate or apply exchange token.');
      setAppliedToken(null);
    }
  };

  // Product Selection helper
  const addToCart = (product: any) => {
    setCart((prev: CartItem[]) => {
      const existing = prev.find((item: CartItem) => item.id === product.id);
      if (existing) {
        return prev.map((item: CartItem) =>
          item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...prev, {
        id: product.id,
        productId: product.productId,
        variantId: product.variantId,
        name: product.name,
        sku: product.sku,
        price: product.price,
        quantity: 1,
        discount: 0,
        gstRate: product.gstRate,
        mrp: product.mrp,
        discount_percentage: product.discount_percentage,
        retail_price: product.price,
        wholesale_price: product.wholesale_price
      }];
    });
  };

  const updateQuantity = (id: string, delta: number) => {
    setCart((prev: CartItem[]) => prev.map((item: CartItem) => {
      if (item.id === id) {
        const nextQty = item.quantity + delta;
        return nextQty > 0 ? { ...item, quantity: nextQty } : item;
      }
      return item;
    }));
  };

  const removeItem = (id: string) => {
    setCart((prev: CartItem[]) => prev.filter((item: CartItem) => item.id !== id));
  };

  const updateDiscount = (id: string, discount: number) => {
    setCart((prev: CartItem[]) => prev.map((item: CartItem) =>
      item.id === id ? { ...item, discount: Math.min(100, Math.max(0, discount)) } : item
    ));
  };

  // ─── Dynamic GST Slab Engine ────────────────────────────────
  // Resolve the effective GST rate for an item based on tenant profile.
  const resolveItemGstRate = (itemPrice: number, catalogRate: number): number => {
    if (!isGstEnabled) return 0;
    const slab = tenantProfile.tax_slabs[0];
    if (!slab) return catalogRate; // fallback to product's own rate
    return itemPrice <= slab.threshold_amount
      ? slab.low_rate_percentage
      : slab.high_rate_percentage;
  };

  // Calculations
  const calculateCart = () => {
    let grossSubtotal = 0;
    const itemTaxableValues: Array<{ taxableValue: number; gstRate: number }> = [];

    cart.forEach((item: CartItem) => {
      const effectiveGstRate = resolveItemGstRate(item.price, item.gstRate);
      const itemSubtotal = item.price * item.quantity;
      const discountAmount = itemSubtotal * (item.discount / 100);
      const taxableValue = itemSubtotal - discountAmount;

      grossSubtotal += taxableValue;
      itemTaxableValues.push({ taxableValue, gstRate: effectiveGstRate });
    });

    // Apply overall discount
    const overallDiscountAmount = Math.min(grossSubtotal, overallDiscount);
    const netSubtotal = grossSubtotal - overallDiscountAmount;

    // Calculate tax proportionally on the discounted values
    let totalGst = 0;
    const gstBreakdown: Record<number, number> = {};
    const discountScale = grossSubtotal > 0 ? (grossSubtotal - overallDiscountAmount) / grossSubtotal : 1;

    itemTaxableValues.forEach((item) => {
      const discountedItemTaxable = item.taxableValue * discountScale;
      const gstAmount = discountedItemTaxable * (item.gstRate / 100);
      totalGst += gstAmount;
      gstBreakdown[item.gstRate] = (gstBreakdown[item.gstRate] || 0) + gstAmount;
    });

    const grandTotalBeforeToken = netSubtotal + totalGst;
    const tokenDeduction = appliedToken ? Math.min(grandTotalBeforeToken, appliedToken.balanceRemaining) : 0;
    const grandTotal = grandTotalBeforeToken - tokenDeduction;

    return {
      subtotal: grossSubtotal,
      overallDiscountAmount,
      netSubtotal,
      totalGst,
      grandTotalBeforeToken,
      tokenDeduction,
      grandTotal,
      gstBreakdown,
    };
  };

  const totals = calculateCart();

  const handleCheckout = async (e: React.FormEvent) => {
    e.preventDefault();
    if (cart.length === 0 || !user?.tenantId) return;

    const payload = {
      invoiceNumber: `INV-${Date.now()}`,
      date: new Date().toISOString(),
      customerName: customerName || 'Walk-in Customer',
      customerPhone: customerPhone.trim() || undefined,
      customerGstin: customerGstin.trim() || undefined,
      customerCity: customerCity.trim() || undefined,
      appliedTokenCode: appliedToken?.tokenCode || undefined,
      items: cart.map((item: CartItem) => {
        const effectiveRate = resolveItemGstRate(item.price, item.gstRate);
        const itemSub = item.price * item.quantity;
        const discountScale = totals.subtotal > 0 ? (totals.subtotal - totals.overallDiscountAmount) / totals.subtotal : 1;
        const taxableValue = (itemSub * (1 - item.discount / 100)) * discountScale;
        const cgst = gstType === 'CGST_SGST' ? (taxableValue * (effectiveRate / 200)) : 0;
        const sgst = gstType === 'CGST_SGST' ? (taxableValue * (effectiveRate / 200)) : 0;
        const igst = gstType === 'IGST' ? (taxableValue * (effectiveRate / 100)) : 0;
        const totalAmount = taxableValue + cgst + sgst + igst;

        return {
          id: item.id,
          productId: item.productId,
          variantId: item.variantId,
          name: item.name,
          sku: item.sku,
          price: item.price,
          quantity: item.quantity,
          discount: item.discount,
          gstRate: effectiveRate,
          mrp: item.mrp || item.price,
          discount_percentage: item.discount_percentage || 0,
          retail_price: item.retail_price || item.price,
          wholesale_price: item.wholesale_price || 0,
          cgstAmount: cgst,
          sgstAmount: sgst,
          igstAmount: igst,
          totalAmount
        };
      }),
      subtotal: totals.subtotal,
      overallDiscount: totals.subtotal > 0 ? Math.round((totals.overallDiscountAmount / totals.subtotal) * 100) : 0,
      overallDiscountAmount: totals.overallDiscountAmount,
      netSubtotal: totals.netSubtotal,
      cgstTotal: gstType === 'CGST_SGST' ? totals.totalGst / 2 : 0,
      sgstTotal: gstType === 'CGST_SGST' ? totals.totalGst / 2 : 0,
      igstTotal: gstType === 'IGST' ? totals.totalGst : 0,
      grandTotal: totals.grandTotalBeforeToken,
      gstType,
      paymentMode,
      status: 'PAID',
    };

    try {
      const response = await api.post('/billing/invoices', payload, {
        headers: { 'x-tenant-id': user.tenantId },
      });
      if (response.data.success) {
        const createdInvoice = response.data.data;
        const mappedInvoice = {
          id: createdInvoice.invoiceNumber || createdInvoice.id,
          date: createdInvoice.invoiceDate || createdInvoice.createdAt,
          customerName: createdInvoice.customerName,
          customerPhone: createdInvoice.customerPhone || 'N/A',
          customerGstin: createdInvoice.customerGstin || undefined,
          subtotal: createdInvoice.subtotal,
          overallDiscount: payload.overallDiscount,
          overallDiscountAmount: createdInvoice.discountTotal,
          netSubtotal: createdInvoice.subtotal - createdInvoice.discountTotal,
          totalGst: createdInvoice.cgstTotal + createdInvoice.sgstTotal + createdInvoice.igstTotal,
          grandTotal: createdInvoice.grandTotal,
          gstType: createdInvoice.cgstTotal > 0 ? 'CGST_SGST' : 'IGST',
          paymentMode: createdInvoice.paymentMode,
          status: createdInvoice.status,
          items: (createdInvoice.items || []).map((item: any) => ({
            ...item,
            name: item.productName || item.name,
            price: item.unitPrice !== undefined ? item.unitPrice : item.price,
          })),
          loyaltyPointsEarned: createdInvoice.loyaltyPointsEarned || 0
        };
        setGeneratedInvoice(mappedInvoice);
        setInvoices([mappedInvoice, ...invoices]);
        setIsReprint(false);
        setShowReceipt(true);
      }
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to submit checkout transaction.');
    }
  };

  const resetTerminal = () => {
    setCart([]);
    setCustomerName('');
    setCustomerPhone('');
    setCustomerGstin('');
    setCustomerCity('');
    setGstType('CGST_SGST');
    setPaymentMode('CASH');
    setOverallDiscount(0);
    setActiveTab('HISTORY');
    setShowReceipt(false);
    setTokenInput('');
    setAppliedToken(null);
    setTokenError('');
    setTokenSuccess('');
    setCrmTokenSuggestion(null);
  };

  // Filter product catalog
  const filteredProducts = searchTerm.trim() === ''
    ? productsList.slice(0, 6)
    : productsList.filter((product: any) =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.sku.toLowerCase().includes(searchTerm.toLowerCase())
      );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-surface-200 pb-4">
        <div>
          <h1 className="text-2xl font-bold text-surface-900 tracking-tight">
            Billing & Invoicing
          </h1>
          <p className="mt-1 text-sm text-surface-500 font-light">
            Create compliance ready sales logs, park pending drafts, and compute CGST/SGST/IGST
            rates.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {activeTab === 'HISTORY' ? (
            <button
              onClick={() => setActiveTab('TERMINAL')}
              className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shadow-sm border border-emerald-500"
            >
              <ShoppingBag size={14} />
              <span>Launch POS Terminal</span>
            </button>
          ) : (
            /* Segmented Controls for active checkout terminal modes */
            <div className="inline-flex rounded-lg border border-slate-200 bg-slate-100 p-0.5 shadow-sm">
              <button
                onClick={() => setActiveTab('TERMINAL')}
                className={`px-3.5 py-1.5 rounded-md text-xs font-bold transition-all cursor-pointer ${activeTab === 'TERMINAL'
                  ? 'bg-white text-slate-800 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
                  }`}
              >
                Billing Terminal
              </button>
              <button
                onClick={() => setActiveTab('DRAFTS')}
                className={`px-3.5 py-1.5 rounded-md text-xs font-bold transition-all cursor-pointer relative ${activeTab === 'DRAFTS'
                  ? 'bg-white text-slate-800 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
                  }`}
              >
                <span>Held Drafts</span>
                {heldBills.length > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-amber-500 text-[8px] font-bold text-white ring-2 ring-white animate-pulse">
                    {heldBills.length}
                  </span>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ─── TERMINAL VIEW ────────────────────────────────────────── */}
      {activeTab === 'TERMINAL' ? (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Main Cart & Catalog (Left 2/3) */}
          <div className="xl:col-span-2 space-y-6">
            {/* Catalog Panel */}
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold text-surface-800 uppercase tracking-wider flex items-center gap-2">
                  <Search size={16} className="text-primary-600" />
                  <span>Product Catalog</span>
                </h3>
                <div className="relative w-64">
                  <Search className="absolute left-3 top-2.5 h-3.5 w-3.5 text-slate-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search by product name or SKU..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      if (searchError) setSearchError('');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const cleanTerm = searchTerm.trim().toUpperCase();
                        let matched = productsList.find((p: any) => p.sku.toUpperCase() === cleanTerm);
                        if (!matched) {
                          const filtered = productsList.filter((product: any) =>
                            product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            product.sku.toLowerCase().includes(searchTerm.toLowerCase())
                          );
                          if (filtered.length === 1) {
                            matched = filtered[0];
                          }
                        }
                        if (matched) {
                          addToCart(matched);
                          setSearchTerm('');
                          setSearchError('');
                        } else {
                          setSearchError(`SKU/Barcode "${cleanTerm}" not in catalog.`);
                          setSearchTerm(''); // Refresh the bar ready for next scan
                        }
                        // Keep cursor/focus on search
                        setTimeout(() => {
                          searchInputRef.current?.focus();
                        }, 50);
                      }
                    }}
                    className="block w-full pl-9 pr-3 py-1.5 text-xs border border-slate-200 rounded bg-slate-50 focus:outline-none focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600"
                  />
                  {searchError && (
                    <div className="absolute right-0 top-9 z-10 w-72 p-2 bg-red-50 border border-red-200 rounded-lg shadow-lg text-[10px] font-bold text-red-700 animate-fadeIn flex items-center justify-between">
                      <span>{searchError}</span>
                      <button onClick={() => setSearchError('')} className="text-red-400 hover:text-red-650 ml-1">
                        <X size={10} />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* High density product cards list */}
              {loadingProducts ? (
                <div className="py-12 text-center text-slate-400 font-bold">
                  <span className="inline-block animate-pulse">
                    Syncing dynamic store catalog...
                  </span>
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="py-12 text-center text-slate-400 font-bold">
                  No products match your search.
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {filteredProducts.map((product) => (
                    <button
                      key={product.id}
                      onClick={() => addToCart(product)}
                      className="p-3 border border-slate-100 hover:border-emerald-500 hover:bg-emerald-50/10 rounded flex flex-col text-left group transition-all duration-150 cursor-pointer active:scale-95 shadow-sm"
                    >
                      <span className="text-[10px] text-slate-400 font-mono tracking-tight">
                        {product.sku}
                      </span>
                      <span className="text-xs font-bold text-slate-800 line-clamp-1 group-hover:text-emerald-700 mt-0.5">
                        {product.name}
                      </span>
                      <div className="flex items-center justify-between w-full mt-2">
                        {product.discount_percentage > 0 ? (
                          <div className="flex flex-col">
                            <div className="flex items-center gap-1">
                              <span className="text-[9px] text-slate-400 line-through font-mono">₹{product.mrp.toFixed(2)}</span>
                              <span className="text-xs font-semibold text-slate-900 font-mono">₹{product.price.toFixed(2)}</span>
                            </div>
                            <span className="text-[8px] bg-red-50 text-red-500 px-1 py-0.5 rounded font-extrabold self-start mt-0.5">[{product.discount_percentage.toFixed(0)}% OFF]</span>
                          </div>
                        ) : (
                          <span className="text-xs font-semibold text-slate-900 font-mono">₹{product.price.toFixed(2)}</span>
                        )}
                        {isGstEnabled && (
                          <span className="text-[9px] px-1.5 py-0.5 bg-slate-100 text-slate-500 font-bold rounded">
                            GST {product.gstRate}%
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Billing Cart Table */}
            <div className="card">
              <h3 className="text-sm font-bold text-surface-800 uppercase tracking-wider mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span>Billing Cart ({cart.length} unique)</span>
                  {cart.length > 0 && (
                    <span className="bg-emerald-100 text-emerald-800 text-[10px] px-2.5 py-0.5 rounded-full font-bold">
                      Total Qty: {cart.reduce((sum, item) => sum + item.quantity, 0)}
                    </span>
                  )}
                </div>
                {cart.length > 0 && (
                  <button
                    onClick={() => setCart([])}
                    className="text-xs text-red-500 hover:underline flex items-center gap-1 font-semibold cursor-pointer"
                  >
                    <Trash2 size={13} />
                    <span>Clear Cart</span>
                  </button>
                )}
              </h3>

              {cart.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <ShoppingBag size={40} className="mx-auto mb-2 opacity-30 text-emerald-600" />
                  <p className="text-xs font-medium">Cart is currently empty.</p>
                  <p className="text-[11px] text-slate-400 mt-1">
                    Select items from the catalog above to build invoice.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-[11px]">
                    <thead>
                      <tr className="border-b border-slate-100 text-slate-400 uppercase tracking-wider text-left bg-slate-50">
                        <th className="py-2 px-3 text-center">S.No.</th>
                        <th className="py-2 px-3">Item Details</th>
                        <th className="py-2 px-2 text-right">Price (₹)</th>
                        <th className="py-2 px-2 text-center">Qty</th>
                        <th className="py-2 px-2 text-center">Disc (%)</th>
                        {isGstEnabled && <th className="py-2 px-2 text-center">GST</th>}
                        <th className="py-2 px-3 text-right">Total (₹)</th>
                        <th className="py-2 px-2 text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {cart.map((item, idx) => {
                        const effectiveRate = resolveItemGstRate(item.price, item.gstRate);
                        const itemSub = item.price * item.quantity;
                        const discVal = itemSub * (item.discount / 100);
                        const taxable = itemSub - discVal;
                        const total = taxable + taxable * (effectiveRate / 100);

                        return (
                          <tr key={item.id} className="hover:bg-slate-50/50">
                            <td className="py-2.5 px-3 text-center font-bold text-slate-500">{idx + 1}</td>
                            <td className="py-2.5 px-3">
                              <span className="font-semibold text-slate-800 block">
                                {item.name}
                              </span>
                              <span className="text-[9px] font-mono text-slate-400 mt-0.5 block">
                                {item.sku}
                              </span>
                            </td>
                            <td className="py-2.5 px-2 text-right font-mono font-medium">
                              {item.discount_percentage && item.discount_percentage > 0 ? (
                                <div className="flex flex-col items-end">
                                  <div className="flex items-center gap-1">
                                    <span className="text-[10px] text-slate-400 line-through">₹{(item.mrp || item.price).toFixed(2)}</span>
                                    {item.discount > 0 ? (
                                      <>
                                        <span className="text-[10px] text-slate-400 line-through">₹{(item.retail_price || item.price).toFixed(2)}</span>
                                        <span className="font-bold text-slate-800">₹{((item.retail_price || item.price) * (1 - item.discount / 100)).toFixed(2)}</span>
                                      </>
                                    ) : (
                                      <span className="font-bold text-slate-800">₹{(item.retail_price || item.price).toFixed(2)}</span>
                                    )}
                                  </div>
                                  <div className="flex flex-col items-end gap-0.5 mt-0.5">
                                    <span className="text-[8px] bg-red-50 text-red-500 px-1 py-0.5 rounded font-extrabold">[{item.discount_percentage.toFixed(0)}% Disc]</span>
                                    {item.discount > 0 && (
                                      <span className="text-[8px] bg-amber-50 text-amber-600 px-1 py-0.5 rounded font-extrabold">[{item.discount}% Extra Disc]</span>
                                    )}
                                  </div>
                                </div>
                              ) : item.discount > 0 ? (
                                <div>
                                  <span className="text-[10px] text-slate-400 line-through block">₹{item.price.toFixed(2)}</span>
                                  <span className="font-bold text-slate-800">₹{(item.price * (1 - item.discount / 100)).toFixed(2)}</span>
                                  <span className="text-[8px] bg-amber-50 text-amber-600 px-1 py-0.5 rounded font-extrabold mt-0.5 block w-max ml-auto">[{item.discount}% Extra Disc]</span>
                                </div>
                              ) : (
                                <span>₹{item.price.toFixed(2)}</span>
                              )}
                            </td>
                            <td className="py-2.5 px-2">
                              <div className="flex items-center justify-center space-x-1.5">
                                <button
                                  onClick={() => updateQuantity(item.id, -1)}
                                  className="w-4 h-4 rounded border border-slate-200 bg-white flex items-center justify-center hover:bg-slate-100 active:scale-90 cursor-pointer"
                                >
                                  <Minus size={8} />
                                </button>
                                <span className="w-6 text-center font-bold text-slate-900">
                                  {item.quantity}
                                </span>
                                <button
                                  onClick={() => updateQuantity(item.id, 1)}
                                  className="w-4 h-4 rounded border border-slate-200 bg-white flex items-center justify-center hover:bg-slate-100 active:scale-90 cursor-pointer"
                                >
                                  <Plus size={8} />
                                </button>
                              </div>
                            </td>
                            <td className="py-2.5 px-2">
                              <input
                                type="number"
                                min="0"
                                max="100"
                                value={item.discount || ''}
                                onChange={(e) =>
                                  updateDiscount(item.id, parseInt(e.target.value) || 0)
                                }
                                className="w-12 text-center py-0.5 border border-slate-200 rounded text-[10px] focus:outline-none focus:border-emerald-600 font-bold"
                              />
                            </td>
                            {isGstEnabled && (
                              <td className="py-2.5 px-2 text-center text-slate-500 font-medium">
                                {effectiveRate}%
                              </td>
                            )}
                            <td className="py-2.5 px-3 text-right font-mono font-semibold text-slate-900">
                              ₹{total.toFixed(2)}
                            </td>
                            <td className="py-2.5 px-2 text-center">
                              <button
                                onClick={() => removeItem(item.id)}
                                className="text-red-400 hover:text-red-600 transition-colors cursor-pointer"
                              >
                                <Trash2 size={13} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Customer & Totals Panel (Right 1/3) */}
          <div className="space-y-6">
            {/* Customer form */}
            <div className="card">
              <h3 className="text-sm font-bold text-surface-800 uppercase tracking-wider mb-4 flex items-center gap-2 border-b border-slate-100 pb-2">
                <User size={15} className="text-primary-600" />
                <span>Customer Registry</span>
              </h3>

              <div className="space-y-3">
                <div className="space-y-1 relative">
                  <label className="text-[10px] font-semibold text-slate-500 uppercase">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    placeholder="Enter 10-digit phone number..."
                    value={customerPhone}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/[^0-9]/g, '');
                      setCustomerPhone(digits.slice(0, 10));
                    }}
                    onFocus={() => {
                      if (customerSuggestions.length > 0) {
                        setShowCustomerDropdown(true);
                      }
                    }}
                    className="block w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded focus:outline-none focus:border-emerald-600 font-medium"
                  />
                  {showCustomerDropdown && customerSuggestions.length > 0 && (
                    <>
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setShowCustomerDropdown(false)}
                      />
                      <div className="absolute left-0 right-0 top-[48px] bg-white border border-slate-250 rounded shadow-md z-50 divide-y divide-slate-100 max-h-40 overflow-y-auto text-xs">
                        {customerSuggestions.map((c) => (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => {
                              setCustomerPhone(c.mobileNumber);
                              setCustomerName(c.name || '');
                              setCustomerCity(c.cityOrPlace || '');
                              setCustomerGstin(c.customerGstin || '');
                              setShowCustomerDropdown(false);
                            }}
                            className="w-full text-left px-2.5 py-1.5 hover:bg-slate-50 flex flex-col justify-start cursor-pointer transition-colors"
                          >
                            <span className="font-semibold text-slate-800">{c.name}</span>
                            <span className="text-[10px] text-slate-500 font-mono">
                              {c.mobileNumber} {c.cityOrPlace ? `(${c.cityOrPlace})` : ''}
                            </span>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-semibold text-slate-500 uppercase">
                    Customer Name
                  </label>
                  <input
                    type="text"
                    placeholder="Enter name..."
                    value={customerName}
                    onChange={(e) => {
                      const lettersOnly = e.target.value.replace(/[0-9]/g, '');
                      setCustomerName(lettersOnly);
                    }}
                    className="block w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded focus:outline-none focus:border-emerald-600"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-semibold text-slate-500 uppercase">
                    City / Place
                  </label>
                  <input
                    type="text"
                    placeholder="Enter city..."
                    value={customerCity}
                    onChange={(e) => setCustomerCity(e.target.value)}
                    className="block w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded focus:outline-none focus:border-emerald-600"
                  />
                </div>
                {isGstEnabled && (
                  <div className="space-y-1">
                    <label className="text-[10px] font-semibold text-slate-500 uppercase">
                      GSTIN (B2B Billing)
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. 07AAAAA1111A1Z1"
                      value={customerGstin}
                      onChange={(e) => setCustomerGstin(e.target.value)}
                      className="block w-full px-2.5 py-1.5 text-xs border border-slate-200 rounded focus:outline-none focus:border-emerald-600"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Calculations & Checkout */}
            <div className="card bg-slate-50 border-slate-200">
              <h3 className="text-sm font-bold text-surface-800 uppercase tracking-wider mb-4 flex items-center gap-2 border-b border-slate-200 pb-2">
                <FileCheck size={15} className="text-primary-600" />
                <span>Billing Summary</span>
              </h3>

              <div className="space-y-3 text-xs">
                {/* GST Type Selector */}
                {isGstEnabled && (
                  <div className="flex justify-between items-center bg-white p-2 rounded border border-slate-100 shadow-sm">
                    <span className="font-semibold text-slate-600">Tax Type</span>
                    <div className="flex space-x-1.5">
                      <button
                        onClick={() => setGstType('CGST_SGST')}
                        className={`px-2 py-1 text-[10px] font-semibold rounded cursor-pointer ${gstType === 'CGST_SGST'
                          ? 'bg-emerald-600 text-white'
                          : 'bg-slate-100 text-slate-600'
                          }`}
                      >
                        CGST + SGST
                      </button>
                      <button
                        onClick={() => setGstType('IGST')}
                        className={`px-2 py-1 text-[10px] font-semibold rounded cursor-pointer ${gstType === 'IGST'
                          ? 'bg-emerald-600 text-white'
                          : 'bg-slate-100 text-slate-600'
                          }`}
                      >
                        IGST
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex justify-between items-center text-slate-650 font-semibold border-b border-slate-100 pb-2">
                  <span>Total Items Qty (Reference)</span>
                  <span className="font-mono text-slate-800 font-bold bg-slate-200/60 px-2 py-0.5 rounded">
                    {cart.reduce((sum, item) => sum + item.quantity, 0)} items
                  </span>
                </div>

                <div className="flex justify-between items-center text-slate-600">
                  <span>Gross Subtotal</span>
                  <span className="font-mono font-medium">₹{totals.subtotal.toFixed(2)}</span>
                </div>

                {/* Overall Discount Section */}
                <div className="flex justify-between items-center bg-white p-2 rounded border border-slate-150 shadow-sm">
                  <span className="font-bold text-slate-700">Overall Discount (₹)</span>
                  <input
                    type="number"
                    min="0"
                    max={totals.subtotal}
                    placeholder="0"
                    value={overallDiscount || ''}
                    onChange={(e) =>
                      setOverallDiscount(Math.min(totals.subtotal, Math.max(0, parseFloat(e.target.value) || 0)))
                    }
                    className="w-20 text-center py-0.5 border border-slate-200 rounded text-xs focus:outline-none focus:border-emerald-600 font-bold"
                  />
                </div>

                {overallDiscount > 0 && (
                  <div className="flex justify-between items-center text-rose-600 text-[11px] font-semibold">
                    <span>Discount Amt</span>
                    <span className="font-mono">-₹{totals.overallDiscountAmount.toFixed(2)}</span>
                  </div>
                )}

                {isGstEnabled && (
                  <>
                    <div className="flex justify-between items-center text-slate-650 font-semibold border-t border-slate-100 pt-2">
                      <span>Total GST (Tax)</span>
                      <span className="font-mono text-slate-800">
                        ₹{totals.totalGst.toFixed(2)}
                      </span>
                    </div>

                    {/* Split Tax Lines */}
                    {gstType === 'CGST_SGST' ? (
                      <>
                        <div className="flex justify-between items-center text-slate-500 text-[11px]">
                          <span>CGST (Central Tax)</span>
                          <span className="font-mono">₹{(totals.totalGst / 2).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center text-slate-500 text-[11px]">
                          <span>SGST (State Tax)</span>
                          <span className="font-mono">₹{(totals.totalGst / 2).toFixed(2)}</span>
                        </div>
                      </>
                    ) : (
                      <div className="flex justify-between items-center text-slate-500 text-[11px]">
                        <span>IGST (Integrated Tax)</span>
                        <span className="font-mono">₹{totals.totalGst.toFixed(2)}</span>
                      </div>
                    )}
                  </>
                )}

                {totals.tokenDeduction > 0 && (
                  <div className="flex justify-between items-center text-emerald-700 text-[11px] font-bold">
                    <span>Exchange Credit Deduction</span>
                    <span className="font-mono">-₹{totals.tokenDeduction.toFixed(2)}</span>
                  </div>
                )}

                <div className="border-t border-slate-200 my-2 pt-2 flex justify-between items-center text-base font-extrabold text-slate-900">
                  <span>Grand Total</span>
                  <span className="font-mono text-emerald-800">
                    ₹{totals.grandTotal.toFixed(2)}
                  </span>
                </div>

                {/* Payment method selector */}
                <div className="space-y-1.5 pt-2">
                  <label className="text-[10px] font-semibold text-slate-500 uppercase">
                    Payment Mode
                  </label>
                  <div className="grid grid-cols-3 gap-1.5">
                    {['CASH', 'CARD', 'UPI'].map((mode: any) => (
                      <button
                        key={mode}
                        type="button"
                        onClick={() => setPaymentMode(mode)}
                        className={`py-1.5 text-[10px] font-semibold rounded border cursor-pointer transition-all ${paymentMode === mode
                          ? 'bg-emerald-600 border-emerald-600 text-white shadow-sm'
                          : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                          }`}
                      >
                        {mode}
                      </button>
                    ))}
                  </div>
                </div>

                {/* CRM Token Notification */}
                {crmTokenSuggestion && !appliedToken && (
                  <div
                    onClick={() => handleApplyToken(crmTokenSuggestion.code)}
                    className="p-2.5 bg-emerald-50 hover:bg-emerald-100/70 border border-emerald-150 rounded-lg text-[10px] text-emerald-850 font-bold flex items-center justify-between gap-1.5 cursor-pointer shadow-sm active:scale-95 transition-all"
                  >
                    <span>[1 Active Exchange Token found for this customer. Click here to apply ₹{crmTokenSuggestion.balance.toFixed(2)} Credit]</span>
                  </div>
                )}

                {/* Token / Coupon code application field */}
                <div className="space-y-1.5 pt-2">
                  <label className="text-[10px] font-bold text-slate-500 uppercase">Apply Exchange Token / Coupon Code</label>
                  <div className="flex gap-1.5">
                    <input
                      type="text"
                      placeholder="e.g. EXC-XXXXXX"
                      value={tokenInput}
                      onChange={(e) => setTokenInput(e.target.value)}
                      className="flex-1 px-2.5 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:border-emerald-600 bg-white uppercase font-mono text-slate-800"
                    />
                    <button
                      type="button"
                      onClick={() => handleApplyToken()}
                      className="px-3 py-1 bg-white hover:bg-slate-50 text-slate-700 text-xs font-bold border border-slate-200 rounded cursor-pointer shadow-sm active:scale-95 transition-all"
                    >
                      Apply
                    </button>
                  </div>
                  {tokenError && (
                    <span className="text-[9px] text-red-500 font-bold block">{tokenError}</span>
                  )}
                  {tokenSuccess && (
                    <span className="text-[9px] text-emerald-650 font-bold block">{tokenSuccess}</span>
                  )}
                </div>

                {/* Actions Button Bar */}
                <div className="flex flex-col gap-2 pt-4">
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        if (cart.length === 0) return;

                        // Park the bill in heldBills
                        const draftId = `DRF-2026-000${heldBills.length + 1}`;
                        const newHeldBill: HeldBill = {
                          id: draftId,
                          timestamp: new Date().toISOString(),
                          customerName: customerName || 'Walk-in Customer',
                          customerPhone: customerPhone || 'N/A',
                          customerGstin: customerGstin || '',
                          customerCity: customerCity || '',
                          cart: [...cart],
                          gstType,
                          paymentMode,
                          overallDiscount,
                        } as any;

                        setHeldBills([newHeldBill, ...heldBills]);

                        // Clear active states
                        setCart([]);
                        setCustomerName('');
                        setCustomerPhone('');
                        setCustomerGstin('');
                        setCustomerCity('');
                        setGstType('CGST_SGST');
                        setPaymentMode('CASH');
                        setOverallDiscount(0);

                        setActiveTab('DRAFTS');
                      }}
                      disabled={cart.length === 0}
                      className="flex-1 py-2 text-xs font-semibold text-amber-800 bg-amber-50 hover:bg-amber-100 disabled:opacity-50 border border-amber-200 rounded active:scale-[0.98] cursor-pointer flex items-center justify-center gap-1 shadow-sm"
                    >
                      Hold Bill
                    </button>
                    <button
                      onClick={() => {
                        setCart([]);
                        setCustomerName('');
                        setCustomerPhone('');
                        setCustomerGstin('');
                        setCustomerCity('');
                        setGstType('CGST_SGST');
                        setPaymentMode('CASH');
                        setOverallDiscount(0);
                        setActiveTab('HISTORY');
                      }}
                      className="flex-1 py-2 text-xs font-semibold text-slate-650 bg-white border border-slate-200 rounded hover:bg-slate-100 active:scale-[0.98] cursor-pointer shadow-sm"
                    >
                      Discard
                    </button>
                  </div>
                  <button
                    onClick={handleCheckout}
                    disabled={cart.length === 0}
                    className="w-full py-2.5 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg active:scale-[0.98] cursor-pointer flex items-center justify-center gap-1.5 border border-emerald-500"
                  >
                    <CreditCard size={14} />
                    <span>Checkout & Print</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : activeTab === 'DRAFTS' ? (
        /* ─── HELD DRAFTS VIEW ────────────────────────────────────── */
        <div className="card">
          <div className="border-b border-slate-100 pb-3 mb-4">
            <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-amber-500 animate-pulse" />
              <span>Held Drafts Parking Lot</span>
            </h2>
            <p className="text-xs text-slate-400 mt-1">
              Parked invoices currently waiting for checkout. Click resume to reload into the active
              billing terminal.
            </p>
          </div>

          {heldBills.length === 0 ? (
            <div className="text-center py-20 text-slate-400 bg-slate-50 rounded-xl border border-dashed border-slate-200">
              <Receipt size={48} className="mx-auto mb-3 opacity-30 text-amber-500" />
              <p className="text-xs font-bold text-slate-700">No bills currently parked on hold.</p>
              <p className="text-[10px] text-slate-400 mt-1">
                Active checkout carts can be paused using the "Hold Bill" button inside the checkout
                terminal.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-slate-200 text-slate-400 font-bold uppercase tracking-wider bg-slate-50">
                    <th className="py-2.5 px-3 text-center">S.No.</th>
                    <th className="py-2.5 px-3">Draft ID</th>
                    <th className="py-2.5 px-2">Parked At</th>
                    <th className="py-2.5 px-2">Customer Details</th>
                    <th className="py-2.5 px-2 text-center">Items Count</th>
                    <th className="py-2.5 px-2 text-right">Draft Subtotal</th>
                    <th className="py-2.5 px-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {heldBills.map((draft, idx) => {
                    const totalQty = draft.cart.reduce((sum, item) => sum + item.quantity, 0);
                    const grossSub = draft.cart.reduce(
                      (sum, item) => sum + item.price * item.quantity * (1 - item.discount / 100),
                      0,
                    );
                    const netSub = grossSub - draft.overallDiscount;

                    return (
                      <tr key={draft.id} className="hover:bg-slate-50/50">
                        <td className="py-3 px-3 text-center font-bold text-slate-500">{idx + 1}</td>
                        <td className="py-3 px-3 font-mono font-bold text-amber-700">{draft.id}</td>
                        <td className="py-3 px-2 text-slate-500">
                          {new Date(draft.timestamp).toLocaleTimeString()}
                        </td>
                        <td className="py-3 px-2">
                          <span className="font-semibold text-slate-800 block">
                            {draft.customerName}
                          </span>
                          <span className="text-[10px] text-slate-400 font-mono mt-0.5 block">
                            {draft.customerPhone}
                          </span>
                        </td>
                        <td className="py-3 px-2 text-center font-semibold font-mono text-slate-700">
                          {totalQty} items
                        </td>
                        <td className="py-3 px-2 text-right font-mono font-bold text-slate-800">
                          ₹{netSub.toFixed(2)}
                        </td>
                        <td className="py-3 px-3">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => {
                                // Load draft properties back into checkout states
                                setCart(draft.cart);
                                setCustomerName(
                                  draft.customerName === 'Walk-in Customer'
                                    ? ''
                                    : draft.customerName,
                                );
                                setCustomerPhone(
                                  draft.customerPhone === 'N/A' ? '' : draft.customerPhone,
                                );
                                setCustomerGstin(draft.customerGstin);
                                setCustomerCity((draft as any).customerCity || '');
                                setGstType(draft.gstType);
                                setPaymentMode(draft.paymentMode);
                                setOverallDiscount(draft.overallDiscount);

                                // Remove draft from hold listing
                                setHeldBills(heldBills.filter((h) => h.id !== draft.id));

                                // Navigate to checkout terminal
                                setActiveTab('TERMINAL');
                              }}
                              className="px-3 py-1 bg-emerald-600 hover:bg-emerald-500 text-white rounded text-[10px] font-bold transition-all shadow-sm cursor-pointer border border-emerald-500"
                            >
                              Resume Draft
                            </button>
                            <button
                              onClick={() => {
                                setHeldBills(heldBills.filter((h) => h.id !== draft.id));
                              }}
                              className="px-2.5 py-1 bg-white hover:bg-slate-100 text-red-650 rounded text-[10px] font-bold transition-all border border-slate-200 cursor-pointer shadow-sm animate-pulse"
                            >
                              Discard
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        /* ─── INVOICE LOGS LIST ────────────────────────────────────── */
        <div className="space-y-6">
          {/* Stats Grid */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="card flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded bg-emerald-50 text-emerald-600 border border-emerald-100">
                <Receipt size={22} />
              </div>
              <div>
                <p className="text-xs text-slate-400 font-bold uppercase tracking-wider">
                  Total Sales Invoices
                </p>
                <p className="text-xl font-bold text-slate-800">{invoices.length}</p>
              </div>
            </div>
            <div className="card flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded bg-emerald-50 text-emerald-600 border border-emerald-100">
                <IndianRupee size={22} />
              </div>
              <div>
                <p className="text-xs text-slate-400 font-bold uppercase tracking-wider">
                  Revenue Captured
                </p>
                <p className="text-xl font-bold text-slate-800">
                  ₹{invoices.reduce((sum, inv) => sum + inv.grandTotal, 0).toFixed(2)}
                </p>
              </div>
            </div>
            <div className="card flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded bg-emerald-50 text-emerald-600 border border-emerald-100">
                <FileText size={22} />
              </div>
              <div>
                <p className="text-xs text-slate-400 font-bold uppercase tracking-wider">
                  Billing Terminals
                </p>
                <p className="text-xl font-bold text-slate-800">2 Active</p>
              </div>
            </div>
          </div>

          {/* Recent Invoices Table */}
          <div className="card">
            <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-4">
              Historical Billing Logs
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-slate-100 text-slate-400 font-bold uppercase tracking-wider bg-slate-50/50">
                    <th className="py-2.5 px-3 text-center">S.No.</th>
                    <th className="py-2.5 px-3">Invoice ID</th>
                    <th className="py-2.5 px-2">Date & Time</th>
                    <th className="py-2.5 px-2">Customer Details</th>
                    <th className="py-2.5 px-2 text-right">Grand Total (₹)</th>
                    <th className="py-2.5 px-2 text-center">Payment</th>
                    <th className="py-2.5 px-2 text-center">Status</th>
                    <th className="py-2.5 px-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {invoices.map((inv, idx) => (
                    <tr key={inv.id} className="hover:bg-slate-50/50">
                      <td className="py-2.5 px-3 text-center font-bold text-slate-500">{idx + 1}</td>
                      <td className="py-2.5 px-3 font-mono font-bold text-emerald-700">{inv.id}</td>
                      <td className="py-2.5 px-2 text-slate-500">
                        {new Date(inv.date).toLocaleString()}
                      </td>
                      <td className="py-2.5 px-2">
                        <span className="font-semibold text-slate-800 block">
                          {inv.customerName}
                        </span>
                        <span className="text-[10px] text-slate-400 font-mono mt-0.5 block">
                          {inv.customerPhone}
                        </span>
                      </td>
                      <td className="py-2.5 px-2 text-right font-mono font-bold text-slate-900">
                        ₹{inv.grandTotal.toFixed(2)}
                      </td>
                      <td className="py-2.5 px-2 text-center">
                        <span className="text-[9px] px-1.5 py-0.5 font-bold bg-slate-100 text-slate-600 rounded">
                          {inv.paymentMode}
                        </span>
                      </td>
                      <td className="py-2.5 px-2 text-center">
                        <span className="text-[9px] px-1.5 py-0.5 font-bold bg-emerald-100 text-emerald-800 rounded">
                          {inv.status}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        <button
                          onClick={() => {
                            setGeneratedInvoice(inv);
                            setIsReprint(true);
                            setShowReceipt(true);
                          }}
                          className="px-2.5 py-1 bg-white hover:bg-slate-100 text-slate-700 rounded border border-slate-200 text-[10px] font-bold transition-all cursor-pointer flex items-center justify-center gap-1 mx-auto shadow-sm active:scale-95"
                        >
                          <Printer size={12} className="text-slate-500" />
                          <span>Reprint</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ─── THERMAL RECEIPT POPUP MODAL ──────────────────────────── */}
      {showReceipt && generatedInvoice && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded border border-slate-200 w-full max-w-sm max-h-[85vh] flex flex-col shadow-2xl animate-scaleUp">
            {/* Modal actions header */}
            <div className="flex justify-between items-center p-3 border-b border-slate-100 bg-slate-50">
              <span className="text-xs font-bold text-slate-800 uppercase tracking-wider">Terminal Print Queue</span>
              <button
                onClick={() => {
                  if (isReprint) {
                    setShowReceipt(false);
                  } else {
                    resetTerminal();
                  }
                }}
                className="text-slate-400 hover:text-slate-600 cursor-pointer"
              >
                <X size={16} />
              </button>
            </div>

            {/* Styles for Receipt roll out animation */}
            <style
              dangerouslySetInnerHTML={{
                __html: `
              @keyframes receiptRollOut {
                0% {
                  max-height: 0px;
                  opacity: 0.2;
                  transform: scaleY(0.01);
                }
                100% {
                  max-height: 2000px;
                  opacity: 1;
                  transform: scaleY(1);
                }
              }
              .animate-receipt-roll {
                animation: receiptRollOut 2s cubic-bezier(0.25, 1, 0.5, 1) forwards;
                transform-origin: top;
              }
            `,
              }}
            />

            {/* 80mm Thermal Receipt Content wrapper */}
            <div className="flex-1 overflow-y-auto p-6 bg-slate-100 flex flex-col items-center">
              {/* POS Printer Machine Top Header Slot */}
              <div className="w-full max-w-[280px] h-9 bg-slate-800 rounded-t-lg border-b-4 border-slate-900 flex flex-col justify-end p-1.5 shadow-inner relative z-10">
                {/* Status LED green light */}
                <div className="absolute top-2.5 left-3 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping" />
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 absolute" />
                  <span className="text-[7px] text-slate-400 font-extrabold tracking-widest uppercase">
                    PRINTING...
                  </span>
                </div>
                {/* Paper slot cut */}
                <div className="w-11/12 mx-auto h-1.5 bg-slate-950 rounded shadow-inner" />
              </div>

              {/* Receipt Body */}
              <div
                className="bg-white p-4 border border-slate-300 w-full font-mono text-[10px] text-slate-950 shadow-2xl leading-tight max-w-[270px] animate-receipt-roll relative"
                style={{
                  backgroundImage:
                    'linear-gradient(to right, rgba(0,0,0,0.02) 0%, rgba(0,0,0,0) 8%, rgba(0,0,0,0) 92%, rgba(0,0,0,0.02) 100%)',
                  borderBottom: '4px dashed #cbd5e1', // Jagged paper cut effect
                }}
              >
                {/* Store details — dynamic from company profile */}
                <div className="text-center space-y-1 mb-4 border-b border-dashed border-slate-300 pb-3">
                  <h4 className="font-bold text-xs uppercase">
                    {tenantProfile.company_name || 'YOUR TEXTILE STORE'}
                  </h4>
                  {tenantProfile.company_address && (
                    <p className="whitespace-pre-line text-[9px] text-slate-600">
                      {tenantProfile.company_address}
                    </p>
                  )}
                  {isGstEnabled && tenantProfile.tenant_gstin && (
                    <p className="font-semibold tracking-wide">
                      GSTIN: {tenantProfile.tenant_gstin}
                    </p>
                  )}
                  {tenantProfile.company_mobile && <p>Tel: +91 {tenantProfile.company_mobile}</p>}
                </div>

                {/* Metadata */}
                <div className="space-y-0.5 border-b border-dashed border-slate-300 pb-2 mb-2">
                  <div className="flex justify-between">
                    <span>INVOICE:</span>
                    <span className="font-bold">{generatedInvoice.id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>DATE:</span>
                    <span>{new Date(generatedInvoice.date).toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>CUSTOMER:</span>
                    <span className="text-right truncate max-w-[120px]">
                      {generatedInvoice.customerName}
                    </span>
                  </div>
                  {isGstEnabled && generatedInvoice.customerGstin && (
                    <div className="flex justify-between">
                      <span>CUST GSTIN:</span>
                      <span className="font-bold">{generatedInvoice.customerGstin}</span>
                    </div>
                  )}
                </div>

                {/* Items list */}
                <div className="space-y-1 border-b border-dashed border-slate-300 pb-2 mb-2">
                  <div className="flex justify-between font-bold">
                    <span className="w-[120px]">ITEM</span>
                    <span className="w-[40px] text-center">QTY</span>
                    <span className="w-[80px] text-right">TOTAL</span>
                  </div>
                  {generatedInvoice.items.map((item: any) => {
                    const itemPrice = item.price || item.unitPrice || 0;
                    const effectiveRate = resolveItemGstRate(itemPrice, item.gstRate);

                    const mrpVal = item.mrp || itemPrice;
                    const labelDiscPct = item.discountPercentage || item.discount_percentage || 0;
                    const finalUnitPrice = itemPrice * (1 - (item.discount || 0) / 100);
                    return (
                      <div key={item.id} className="flex justify-between border-b border-slate-50 pb-1.5 mb-1.5 last:border-0 last:pb-0 last:mb-0">
                        <div className="w-[120px]">
                          <span className="font-semibold block">{item.name}</span>
                          <span className="text-[8px] text-slate-500 font-normal">
                            {item.sku}
                            {isGstEnabled && ` (GST ${effectiveRate}%)`}
                          </span>
                          <div className="text-[8px] text-slate-650 font-normal mt-0.5 space-y-0.5">
                            <div>MRP: <span className={mrpVal > itemPrice ? 'line-through' : ''}>₹{mrpVal.toFixed(2)}</span></div>
                            {labelDiscPct > 0 && (
                              <div className="text-slate-900 font-bold">[{labelDiscPct.toFixed(0)}% Disc]</div>
                            )}
                            {item.discount > 0 && (
                              <div className="text-slate-900 font-bold">[{item.discount}% Extra Disc]</div>
                            )}
                          </div>
                        </div>
                        <span className="w-[40px] text-center pt-0.5">{item.quantity}</span>
                        <span className="w-[80px] text-right pt-0.5 font-semibold">₹{finalUnitPrice.toFixed(2)}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Calculations */}
                <div className="space-y-1 border-b border-dashed border-slate-300 pb-2 mb-2">
                  <div className="flex justify-between">
                    <span>GROSS VALUE:</span>
                    <span>₹{generatedInvoice.subtotal.toFixed(2)}</span>
                  </div>
                  {generatedInvoice.overallDiscount > 0 && (
                    <div className="flex justify-between text-slate-950 font-bold">
                      <span>OVERALL DISC ({generatedInvoice.overallDiscount}%):</span>
                      <span>-₹{generatedInvoice.overallDiscountAmount.toFixed(2)}</span>
                    </div>
                  )}
                  {generatedInvoice.overallDiscount > 0 && (
                    <div className="flex justify-between font-semibold">
                      <span>NET SUB-TOTAL:</span>
                      <span>₹{generatedInvoice.netSubtotal.toFixed(2)}</span>
                    </div>
                  )}
                  {/* Conditional GST lines — only when GST is enabled */}
                  {isGstEnabled &&
                    generatedInvoice.totalGst > 0 &&
                    (generatedInvoice.gstType === 'CGST_SGST' ? (
                      <>
                        <div className="flex justify-between">
                          <span>CGST (CENTRAL TAX):</span>
                          <span>₹{(generatedInvoice.totalGst / 2).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>SGST (STATE TAX):</span>
                          <span>₹{(generatedInvoice.totalGst / 2).toFixed(2)}</span>
                        </div>
                      </>
                    ) : (
                      <div className="flex justify-between">
                        <span>IGST (INTEGRATED):</span>
                        <span>₹{generatedInvoice.totalGst.toFixed(2)}</span>
                      </div>
                    ))}
                  <div className="flex justify-between font-bold text-xs pt-1 border-t border-slate-100">
                    <span>NET AMOUNT:</span>
                    <span>₹{generatedInvoice.grandTotal.toFixed(2)}</span>
                  </div>
                </div>

                {/* Loyalty points info */}
                {generatedInvoice.loyaltyPointsEarned > 0 && (
                  <div className="text-center bg-slate-50 border border-slate-200 p-2 rounded my-2">
                    <p className="font-bold text-slate-900 text-[8px] uppercase tracking-wider">
                      🎉 Loyalty Points Earned 🎉
                    </p>
                    <p className="font-black text-slate-900 text-[11px] mt-0.5">
                      +{generatedInvoice.loyaltyPointsEarned} POINTS
                    </p>
                  </div>
                )}

                {/* Payment info */}
                <div className="text-center space-y-1 pt-2">
                  <p className="font-bold">PAYMENT TYPE: {generatedInvoice.paymentMode}</p>
                  <p className="text-[8px] text-slate-500">Thank you for your visit!</p>
                </div>
              </div>
            </div>

            {/* Modal actions footer */}
            <div className="p-3 border-t border-slate-100 bg-slate-50 flex flex-col gap-1.5">
              {printerError && (
                <p className="text-[10px] text-red-500 font-bold text-center mt-1">
                  {printerError}
                </p>
              )}
              {isPrinterConnected && (
                <p className="text-[9px] text-slate-500 text-center">
                  Connected to: <span className="font-bold text-slate-700">{pairedDevice}</span>
                </p>
              )}
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    if (isReprint) {
                      setShowReceipt(false);
                    } else {
                      resetTerminal();
                    }
                  }}
                  className="flex-1 py-1.5 text-xs font-semibold text-slate-650 bg-white border border-slate-200 rounded hover:bg-slate-100 cursor-pointer"
                >
                  Back
                </button>
                {tenantProfile.print_config?.connection_mode === 'USB' ? (
                  <button
                    onClick={() => {
                      const printWindow = window.open('', '_blank');
                      if (!printWindow) return;

                      // Items list HTML
                      const itemsHtml = generatedInvoice.items.map((item: any) => {
                        const itemPrice = item.price || item.unitPrice || 0;
                        const effectiveRate = resolveItemGstRate(itemPrice, item.gstRate);
                        const mrpVal = item.mrp || itemPrice;
                        const labelDiscPct = item.discountPercentage || item.discount_percentage || 0;
                        const finalUnitPrice = itemPrice * (1 - (item.discount || 0) / 100);

                        return `
                          <div style="border-bottom: 1px solid #000; padding-bottom: 4px; margin-bottom: 4px; font-family: monospace;">
                            <div style="font-weight: 900; font-size: 8.5px; text-transform: uppercase;">${item.name}</div>
                            <div style="font-size: 7.5px; color: #000;">
                              SKU: ${item.sku}
                              ${isGstEnabled ? ` | GST: ${effectiveRate}%` : ''}
                            </div>
                            <div style="font-size: 7px; color: #000;">MRP: ${mrpVal > itemPrice ? '<span style="text-decoration: line-through;">' : ''}₹${mrpVal.toFixed(2)}${mrpVal > itemPrice ? '</span>' : ''}</div>
                            ${labelDiscPct > 0 ? `<div style="font-size: 7px; color: #000; font-weight: bold;">[${labelDiscPct.toFixed(0)}% Disc]</div>` : ''}
                            ${item.discount > 0 ? `<div style="font-size: 7px; color: #000; font-weight: bold;">[${item.discount}% Extra Disc]</div>` : ''}
                            <div style="display: flex; justify-content: space-between; font-size: 8px; margin-top: 2px;">
                              <span>${item.quantity} x ₹${finalUnitPrice.toFixed(2)}</span>
                              <span style="font-weight: bold;">₹${(finalUnitPrice * item.quantity).toFixed(2)}</span>
                            </div>
                          </div>
                        `;
                      }).join('');

                      // GST details HTML
                      let gstHtml = '';
                      if (isGstEnabled && generatedInvoice.totalGst > 0) {
                        if (generatedInvoice.gstType === 'CGST_SGST') {
                          gstHtml = `
                            <div style="display: flex; justify-content: space-between; font-size: 8px; margin-top: 2px;">
                              <span>CGST (CENTRAL TAX):</span>
                              <span>₹${(generatedInvoice.totalGst / 2).toFixed(2)}</span>
                            </div>
                            <div style="display: flex; justify-content: space-between; font-size: 8px; margin-top: 2px;">
                              <span>SGST (STATE TAX):</span>
                              <span>₹${(generatedInvoice.totalGst / 2).toFixed(2)}</span>
                            </div>
                          `;
                        } else {
                          gstHtml = `
                            <div style="display: flex; justify-content: space-between; font-size: 8px; margin-top: 2px;">
                              <span>IGST (INTEGRATED):</span>
                              <span>₹${generatedInvoice.totalGst.toFixed(2)}</span>
                            </div>
                          `;
                        }
                      }

                      // Loyalty points HTML
                      let loyaltyHtml = '';
                      if (generatedInvoice.loyaltyPointsEarned > 0) {
                        loyaltyHtml = `
                          <div style="text-align: center; border: 1px solid #000; padding: 4px; border-radius: 4px; margin: 6px 0;">
                            <div style="font-weight: bold; color: #000; font-size: 7.5px; letter-spacing: 0.5px;">🎉 LOYALTY POINTS EARNED 🎉</div>
                            <div style="font-weight: 900; color: #000; font-size: 9px; margin-top: 2px;">+${generatedInvoice.loyaltyPointsEarned} POINTS</div>
                          </div>
                        `;
                      }

                      printWindow.document.write(`
                        <html>
                          <head>
                            <title>Receipt - ${generatedInvoice.id}</title>
                            <style>
                              html, body {
                                width: 50mm;
                                max-width: 50mm;
                                margin: 0;
                                padding: 0;
                                font-family: monospace;
                                font-size: 8.5px;
                                color: #000;
                                background-color: #fff;
                              }
                              .receipt-container {
                                width: 50mm;
                                max-width: 50mm;
                                box-sizing: border-box;
                                padding: 2mm 1mm;
                                margin: 0 auto;
                              }
                              .header {
                                text-align: center;
                                border-bottom: 1px solid #000;
                                padding-bottom: 5px;
                                margin-bottom: 5px;
                              }
                              .header h4 {
                                margin: 0 0 2px 0;
                                font-size: 10px;
                                font-weight: bold;
                                text-transform: uppercase;
                              }
                              .header p {
                                margin: 1px 0 0 0;
                                font-size: 7.5px;
                                color: #000;
                              }
                              .meta {
                                border-bottom: 1px solid #000;
                                padding-bottom: 3px;
                                margin-bottom: 5px;
                              }
                              .meta-row {
                                display: flex;
                                justify-content: space-between;
                                font-size: 7.5px;
                                margin-bottom: 1px;
                              }
                              .totals {
                                border-top: 1px solid #000;
                                padding-top: 3px;
                                margin-top: 5px;
                              }
                              .totals-row {
                                display: flex;
                                justify-content: space-between;
                                font-size: 8px;
                                margin-bottom: 1px;
                              }
                              .grand-total {
                                display: flex;
                                justify-content: space-between;
                                font-weight: bold;
                                font-size: 9.5px;
                                border-top: 1px solid #000;
                                padding-top: 3px;
                                margin-top: 3px;
                              }
                              .footer {
                                text-align: center;
                                margin-top: 8px;
                                font-size: 7.5px;
                                border-top: 1px solid #000;
                                padding-top: 4px;
                              }
                              @page {
                                margin: 0 !important;
                              }
                              @media print {
                                html, body {
                                  width: 50mm;
                                  max-width: 50mm;
                                  height: auto !important;
                                  min-height: 0 !important;
                                  overflow: visible !important;
                                  background: none;
                                }
                                .receipt-container {
                                  border: none;
                                  width: 50mm;
                                  max-width: 50mm;
                                  padding: 1mm 0.5mm;
                                  height: auto !important;
                                  min-height: 0 !important;
                                  page-break-inside: avoid !important;
                                  break-inside: avoid !important;
                                }
                                .header, .meta, .items, .totals, .footer {
                                  page-break-inside: avoid !important;
                                  break-inside: avoid !important;
                                }
                              }
                            </style>
                          </head>
                          <body>
                            <div class="receipt-container">
                              <div class="header">
                                <h4>${tenantProfile.company_name || 'TEXTILE STORE'}</h4>
                                ${tenantProfile.company_address ? `<p style="white-space: pre-line;">${tenantProfile.company_address}</p>` : ''}
                                ${isGstEnabled && tenantProfile.tenant_gstin ? `<p style="font-weight: bold;">GSTIN: ${tenantProfile.tenant_gstin}</p>` : ''}
                                ${tenantProfile.company_mobile ? `<p>Tel: +91 ${tenantProfile.company_mobile}</p>` : ''}
                              </div>
                              
                              <div class="meta">
                                <div class="meta-row">
                                  <span>INVOICE:</span>
                                  <span style="font-weight: bold;">${generatedInvoice.id}</span>
                                </div>
                                <div class="meta-row">
                                  <span>DATE:</span>
                                  <span>${new Date(generatedInvoice.date).toLocaleDateString()}</span>
                                </div>
                                <div class="meta-row">
                                  <span>CUSTOMER:</span>
                                  <span>${generatedInvoice.customerName}</span>
                                </div>
                                ${isGstEnabled && generatedInvoice.customerGstin ? `
                                  <div class="meta-row">
                                    <span>CUST GSTIN:</span>
                                    <span style="font-weight: bold;">${generatedInvoice.customerGstin}</span>
                                  </div>
                                ` : ''}
                              </div>
                              
                              <div class="items">
                                ${itemsHtml}
                              </div>
                              
                              <div class="totals">
                                <div class="totals-row">
                                  <span>GROSS VALUE:</span>
                                  <span>₹${generatedInvoice.subtotal.toFixed(2)}</span>
                                </div>
                                ${generatedInvoice.overallDiscount > 0 ? `
                                  <div class="totals-row" style="color: #000; font-weight: bold;">
                                    <span>OVERALL DISC (${generatedInvoice.overallDiscount}%):</span>
                                    <span>-₹${generatedInvoice.overallDiscountAmount.toFixed(2)}</span>
                                  </div>
                                  <div class="totals-row" style="font-weight: bold;">
                                    <span>NET SUB-TOTAL:</span>
                                    <span>₹${generatedInvoice.netSubtotal.toFixed(2)}</span>
                                  </div>
                                ` : ''}
                                
                                ${gstHtml}
                                
                                ${loyaltyHtml}
                                
                                <div class="grand-total">
                                  <span>NET AMOUNT:</span>
                                  <span>₹${generatedInvoice.grandTotal.toFixed(2)}</span>
                                </div>
                                <div class="totals-row" style="margin-top: 3px; font-weight: bold; font-size: 7.5px;">
                                  <span>PAYMENT:</span>
                                  <span>${generatedInvoice.paymentMode}</span>
                                </div>
                              </div>
                              
                              <div class="footer">
                                ${tenantProfile.print_config?.footer_message || 'Thank you for your visit!'}
                              </div>
                            </div>
                            <script>
                              window.onload = function() {
                                try {
                                  const container = document.querySelector('.receipt-container');
                                  if (container) {
                                    const heightPx = container.getBoundingClientRect().height || container.offsetHeight;
                                    const heightMm = Math.ceil(heightPx * 0.264583) + 5;
                                    
                                    const style = document.createElement('style');
                                    style.innerHTML = '@page { size: 50mm ' + heightMm + 'mm; margin: 0 !important; }';
                                    document.head.appendChild(style);
                                  }
                                } catch (e) {
                                  console.error("Dynamic page size calculation failed:", e);
                                }
                                setTimeout(() => {
                                  window.print();
                                  window.close();
                                }, 150);
                              };
                            </script>
                          </body>
                        </html>
                      `);
                      printWindow.document.close();
                    }}
                    className="flex-1 py-1.5 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded shadow-sm flex items-center justify-center gap-1.5 cursor-pointer border border-emerald-500"
                  >
                    <Printer size={14} />
                    <span>Print Receipt (USB)</span>
                  </button>
                ) : (
                  <button
                    onClick={async () => {
                      if (!isPrinterConnected) {
                        const ok = await connectPrinter();
                        if (!ok) return;
                      }
                      const byteData = compileWirelessReceipt(generatedInvoice, tenantProfile);
                      await sendPrintJob(byteData);
                    }}
                    disabled={isPrinterPrinting}
                    className="flex-1 py-1.5 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded shadow-sm flex items-center justify-center gap-1 cursor-pointer disabled:opacity-50 border border-emerald-500"
                  >
                    <Bluetooth size={12} />
                    <span>
                      {isPrinterPrinting
                        ? 'Printing...'
                        : isPrinterConnected
                          ? 'Print Wireless'
                          : 'Connect Wireless'}
                    </span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
